/**
 * Hook de acompanhamento contínuo do status do job (R19.6/R19.8).
 *
 * Consulta `GET /jobs/{id}` com backoff (ver `lib/polling.ts`) até um estado
 * terminal (DONE/FAILED). Tolera falhas de rede transitórias até um limite,
 * expondo um estado de "reconectando" para a UI, e nunca deixa a aplicação
 * travar: qualquer erro persistente vira um estado de erro tratável, com opção
 * de retomar.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import type { JobStatusResponse, MakePubApiClient } from "../lib/apiClient";
import { ApiError, NetworkError } from "../lib/apiClient";
import { isTerminal } from "../lib/status";
import {
  computeBackoffDelay,
  DEFAULT_BACKOFF,
  MAX_CONSECUTIVE_FAILURES,
} from "../lib/polling";

export type PollingPhase = "idle" | "polling" | "reconnecting" | "terminal" | "error";

export interface JobPollingState {
  readonly phase: PollingPhase;
  readonly job: JobStatusResponse | null;
  /** Mensagem de erro amigável quando `phase === "error"`. */
  readonly error: string | null;
  /** Código estável do erro (para a UI decidir orientação). */
  readonly errorCode: string | null;
  /** Retoma o acompanhamento após um erro. */
  readonly retry: () => void;
}

/**
 * Inicia o polling para `jobId` usando `client`. Passar `jobId = null` mantém o
 * hook ocioso (útil antes de o job existir).
 */
export function useJobPolling(
  client: MakePubApiClient | null,
  jobId: string | null,
): JobPollingState {
  const [job, setJob] = useState<JobStatusResponse | null>(null);
  const [phase, setPhase] = useState<PollingPhase>("idle");
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  // `nonce` força reinício do efeito ao chamar retry().
  const [nonce, setNonce] = useState(0);

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const retry = useCallback(() => {
    setError(null);
    setErrorCode(null);
    setNonce((n) => n + 1);
  }, []);

  useEffect(() => {
    if (!client || !jobId) {
      setPhase("idle");
      return;
    }

    let cancelled = false;
    let attempt = 0;
    let consecutiveFailures = 0;

    const clearTimer = () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    const schedule = (delayMs: number) => {
      clearTimer();
      timerRef.current = setTimeout(tick, delayMs);
    };

    const tick = async () => {
      if (cancelled) {
        return;
      }
      try {
        const result = await client.getJob(jobId);
        if (cancelled) {
          return;
        }
        consecutiveFailures = 0;
        setJob(result);

        if (isTerminal(result.status)) {
          setPhase("terminal");
          clearTimer();
          return;
        }
        setPhase("polling");
        attempt += 1;
        schedule(computeBackoffDelay(attempt, DEFAULT_BACKOFF));
      } catch (err) {
        if (cancelled) {
          return;
        }
        // Erros de aplicação (ex.: 404/401) são definitivos: não adianta insistir.
        if (err instanceof ApiError) {
          setPhase("error");
          setErrorCode(err.code);
          setError(err.message);
          clearTimer();
          return;
        }
        // Erros de rede transitórios: tolera até o limite (reconectando).
        if (err instanceof NetworkError) {
          consecutiveFailures += 1;
          if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
            setPhase("error");
            setErrorCode("NetworkError");
            setError(
              "Perdemos a conexão com o serviço. Verifique sua internet e tente retomar.",
            );
            clearTimer();
            return;
          }
          setPhase("reconnecting");
          schedule(computeBackoffDelay(consecutiveFailures, DEFAULT_BACKOFF));
          return;
        }
        // Erro inesperado: reporta sem travar.
        setPhase("error");
        setErrorCode("Unknown");
        setError("Ocorreu um erro inesperado ao acompanhar o processo.");
        clearTimer();
      }
    };

    setPhase("polling");
    // Primeira consulta imediata.
    void tick();

    return () => {
      cancelled = true;
      clearTimer();
    };
  }, [client, jobId, nonce]);

  return { phase, job, error, errorCode, retry };
}
